import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Incident, IncidentComment, IncidentLinkedItem, IncidentKanbanLink, IncidentWorkspaceSummary } from '../types/incident';
import { Evidence, TicketLink } from '../types/player';
import { incidentsApiService } from '../services/incidentsApi';
import { IncidentAssigneesSection } from './incidents/IncidentAssigneesSection';
import { IncidentWatcherButton } from './incidents/IncidentWatcherButton';
import { shouldUseSyncForIncident } from '../utils/incidentSyncPolicy';
import { kanbanTeamColor } from '../utils/kanbanTeamColors';
import { KanbanSquare, ExternalLink } from 'lucide-react';
import { notesApiService } from '../services/notesApi';
import { MarkdownRenderer } from './MarkdownRenderer';
import { LinkUserModal } from './LinkUserModal';
import { LinkItemModal } from './LinkItemModal';
import { LinkExistingEvidenceModal } from './LinkExistingEvidenceModal';
import { ConfidentialEvidencePlaceholder } from './ConfidentialEvidencePlaceholder';
import { ConfidentialBadge } from './ConfidentialBadge';
import { isEvidenceRedacted, getEvidenceViewUrl } from '../utils/evidence';
import { 
  X,
  Edit, 
  Trash2, 
  MessageSquare, 
  Loader,
  FileText,
  TriangleAlert,
  Axe,
  Users,
  Plus,
  Unlink,
  Upload,
  File,
  Eye,
  Ticket,
  Paperclip,
  Lock
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../utils/cn';
import { useAuth } from '../contexts/AuthContext';
import { CreatorProfile } from './CreatorProfile';
import { PlayerProfile } from './PlayerProfile';
import { ConfirmationModal } from './ConfirmationModal';
import { TicketIdLink } from './TicketIdLink';
import { EditIncidentModal } from './EditIncidentModal';
import { motion, AnimatePresence } from 'framer-motion';
import MarkdownEditor from './MarkdownEditor';
import { SectionHeader } from './ui/SectionHeader';
import { SkeletonLoader } from './SkeletonLoader';

const STATUS_BADGE: Record<string, string> = {
  critical: 'bg-red-500/15 text-red-300 border-red-500/25',
  active: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
  resolved: 'bg-green-500/15 text-green-300 border-green-500/25',
  closed: 'bg-gray-500/15 text-gray-300 border-gray-500/25',
  pending: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/25',
  investigating: 'bg-purple-500/15 text-purple-300 border-purple-500/25',
};

interface IncidentDetailModalProps {
  incidentId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export const IncidentDetailModal = ({ incidentId, isOpen, onClose, onUpdate }: IncidentDetailModalProps) => {
  const { identity } = useAuth();
  const [incident, setIncident] = useState<Incident | null>(null);
  const [comments, setComments] = useState<IncidentComment[]>([]);
  const [linkedItems, setLinkedItems] = useState<IncidentLinkedItem[]>([]);
  const [linkedUsers, setLinkedUsers] = useState<{ user_uuid: string; linked_by: string; linked_at: number }[]>([]);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [tickets, setTickets] = useState<TicketLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUserLinkModalOpen, setIsUserLinkModalOpen] = useState(false);
  const [isItemLinkModalOpen, setIsItemLinkModalOpen] = useState(false);
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
  const [uploadConfidential, setUploadConfidential] = useState(false);
  const [isLinkEvidenceOpen, setIsLinkEvidenceOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<Evidence | null>(null);
  const [newTicketId, setNewTicketId] = useState('');
  const [isLinkingTicket, setIsLinkingTicket] = useState(false);

  // ── Bridge / sync state ─────────────────────────────────────────────────
  const [kanbanLinks, setKanbanLinks] = useState<IncidentKanbanLink[]>([]);
  const [workspacesSummary, setWorkspacesSummary] = useState<IncidentWorkspaceSummary[]>([]);
  const useSync = useMemo(() => shouldUseSyncForIncident(kanbanLinks), [kanbanLinks]);

  const canManage = useMemo(() => {
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Notes') || groups.includes('SPY_Notes_Editor');
  }, [identity]);

  const canDelete = useMemo(() => {
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Super_admin');
  }, [identity]);

  useEffect(() => {
    if (isOpen && incidentId) {
      fetchData();
    }
  }, [isOpen, incidentId]);

  const fetchData = async () => {
    if (!incidentId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [
        incidentData,
        commentsData,
        itemsData,
        usersData,
        evidenceData,
        ticketsData,
        linksData,
        workspacesData,
      ] = await Promise.all([
        incidentsApiService.getIncident(incidentId),
        incidentsApiService.getComments(incidentId),
        incidentsApiService.getItemsForIncident(incidentId),
        incidentsApiService.getUsersForIncident(incidentId),
        incidentsApiService.getEvidenceForIncident(incidentId),
        notesApiService.getTicketsForItem(incidentId),
        incidentsApiService.listKanbanLinks(incidentId).catch(() => [] as IncidentKanbanLink[]),
        incidentsApiService
          .listIncidentWorkspaces(incidentId)
          .catch(() => [] as IncidentWorkspaceSummary[]),
      ]);

      setIncident(incidentData);
      setComments(commentsData);
      setLinkedItems(itemsData);
      setLinkedUsers(usersData);
      setEvidence(evidenceData);
      setTickets(ticketsData);
      setKanbanLinks(linksData);
      setWorkspacesSummary(workspacesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incident');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !incidentId) return;

    setIsSubmittingComment(true);
    try {
      const comment = useSync
        ? await incidentsApiService.addCommentWithSync(incidentId, newComment)
        : await incidentsApiService.addComment(incidentId, newComment);
      setComments(prev => [...prev, comment]);
      setNewComment('');
    } catch (err) {
      alert('Failed to add comment');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleDelete = async () => {
    if (!incidentId) return;
    
    setIsDeleting(true);
    try {
      await incidentsApiService.deleteIncident(incidentId);
      onUpdate?.();
      onClose();
    } catch (err) {
      alert('Failed to delete incident');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleUnlinkUser = async (userUuid: string) => {
    if (!incidentId) return;
    
    try {
      await incidentsApiService.unlinkUser(incidentId, userUuid);
      setLinkedUsers(prev => prev.filter(u => u.user_uuid !== userUuid));
    } catch (err) {
      alert('Failed to unlink user');
    }
  };

  const handleUploadEvidence = async (file: File) => {
    if (!incidentId) return;
    
    setIsUploadingEvidence(true);
    try {
      const uploadedEvidence = await incidentsApiService.uploadEvidence(incidentId, file, uploadConfidential || undefined);
      setEvidence(prev => [...prev, uploadedEvidence]);
    } catch (err) {
      alert('Failed to upload evidence');
    } finally {
      setIsUploadingEvidence(false);
    }
  };

  const handleLinkExistingEvidence = async (evidenceId: string) => {
    if (!incidentId) return;
    
    await incidentsApiService.linkEvidence(incidentId, evidenceId);
    // Refresh evidence list
    const updatedEvidence = await incidentsApiService.getEvidenceForIncident(incidentId);
    setEvidence(updatedEvidence);
  };

  const handleUnlinkEvidence = async (evidenceId: string) => {
    if (!incidentId) return;
    
    try {
      await incidentsApiService.unlinkEvidence(incidentId, evidenceId);
      setEvidence(prev => prev.filter(e => e.id !== evidenceId));
    } catch (err) {
      alert('Failed to unlink evidence');
    }
  };

  const handleUnlinkItem = async (itemId: string) => {
    if (!incidentId) return;
    
    try {
      await incidentsApiService.unlinkItem(incidentId, itemId);
      setLinkedItems(prev => prev.filter(item => item.item_id !== itemId));
    } catch (err) {
      alert('Failed to unlink item');
    }
  };

  const handleLinkTicket = async () => {
    if (!newTicketId.trim() || !incidentId) return;
    
    const ticketId = parseInt(newTicketId, 10);
    if (isNaN(ticketId) || ticketId <= 0) {
      alert('Invalid ticket ID');
      return;
    }

    setIsLinkingTicket(true);
    try {
      await notesApiService.linkTicket(ticketId, incidentId, 'incident');
      setNewTicketId('');
      fetchData();
    } catch (err) {
      alert('Failed to link ticket');
    } finally {
      setIsLinkingTicket(false);
    }
  };

  const handleUnlinkTicket = async (ticketId: number) => {
    if (!incidentId) return;
    
    try {
      await notesApiService.unlinkTicket(ticketId, incidentId);
      setTickets(prev => prev.filter(t => t.ticket_id !== ticketId));
    } catch (err) {
      alert('Failed to unlink ticket');
    }
  };

  const getItemIcon = (itemType: string) => {
    switch (itemType) {
      case 'note': return <FileText className="w-4 h-4 text-blue-400" />;
      case 'warning': return <TriangleAlert className="w-4 h-4 text-yellow-400" />;
      case 'ban': return <Axe className="w-4 h-4 text-red-400" />;
      default: return null;
    }
  };

  const getPriorityColor = (priority: number) => {
    if (priority <= 2) return "bg-red-500/15 text-red-300 border-red-500/25";
    if (priority <= 4) return "bg-orange-500/15 text-orange-300 border-orange-500/25";
    if (priority <= 6) return "bg-yellow-500/15 text-yellow-300 border-yellow-500/25";
    if (priority <= 8) return "bg-blue-500/15 text-blue-300 border-blue-500/25";
    return "bg-gray-500/15 text-gray-300 border-gray-500/25";
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="modal-panel overflow-hidden max-w-5xl"
          onClick={e => e.stopPropagation()}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', duration: 0.3 }}
        >
          {/* Header */}
          <button onClick={onClose} className="modal-close" title="Close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
          <div className="modal-header flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <h2 className="modal-title truncate max-w-full">{incident?.title || 'Loading...'}</h2>
                {loading && <Loader className="w-4 h-4 animate-spin text-glass-400" aria-label="Loading incident" />}
                {incident && (
                  <>
                    <span className={cn("badge badge-dot", STATUS_BADGE[incident.status] ?? 'badge-neutral')}>
                      {incident.status.charAt(0).toUpperCase() + incident.status.slice(1)}
                    </span>
                    <span className={cn("badge", getPriorityColor(incident.priority))} title={`Priority ${incident.priority}`}>
                      P{incident.priority}
                    </span>
                  </>
                )}
              </div>
              {incident && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-glass-400 mt-1 tabular-nums">
                  <span>ID: <span className="font-mono">{incident.id}</span></span>
                  <span>Created {formatDistanceToNow(new Date(incident.created_at), { addSuffix: true })}</span>
                  <span>By <CreatorProfile creatorId={incident.creator_id} inline /></span>
                  {incident.updated_at && (
                    <span>Updated {formatDistanceToNow(new Date(incident.updated_at), { addSuffix: true })}</span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center flex-wrap gap-2 shrink-0">
              {incidentId && (
                <IncidentWatcherButton incidentId={incidentId} />
              )}
              {canManage && (
                <button onClick={() => setIsEditing(true)} className="glass-button">
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
              )}
              {canDelete && (
                <button onClick={() => setShowDeleteConfirm(true)} className="btn-danger">
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              )}
            </div>
          </div>

          {/* Content - Sidebar Layout */}
          <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden -mx-1 px-1">
            {/* Main Content */}
            <div className="flex-1 min-w-0 md:overflow-y-auto md:pr-5 space-y-4">
            {loading && (
              <div className="space-y-4" aria-busy="true">
                <div className="subcard p-4 space-y-2">
                  <SkeletonLoader className="h-4 w-28" />
                  <SkeletonLoader className="h-3 w-full" />
                  <SkeletonLoader className="h-3 w-4/5" />
                </div>
                <div className="subcard p-4 space-y-2">
                  <SkeletonLoader className="h-4 w-28" />
                  <SkeletonLoader className="h-16 w-full" />
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger" role="alert">
                <span>{error}</span>
              </div>
            )}

            {incident && (
              <>
                {/* Description */}
                <div className="subcard p-4">
                  <SectionHeader as="h2" title="Description" />
                  <MarkdownRenderer content={incident.description} />
                </div>

                {/* Comments */}
                <div className="subcard p-4">
                  <SectionHeader
                    as="h2"
                    title="Comments"
                    icon={MessageSquare}
                    count={comments.length}
                    actions={useSync ? (
                      <span
                        className="badge badge-purple"
                        title="At least one bridge link has a sync flag on. Comments will use the with-sync endpoint and mirror to linked kanban cards (when sync_comments is on)."
                      >
                        Sync on
                      </span>
                    ) : undefined}
                  />

                  {/* Add Comment */}
                  {canManage && (
                    <div className="mb-4">
                      <MarkdownEditor
                        value={newComment}
                        onChange={setNewComment}
                        height={200}
                        placeholder="Enter comment in markdown..."
                      />
                      <button
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || isSubmittingComment}
                        className="btn-primary mt-3"
                      >
                        {isSubmittingComment ? 'Adding...' : 'Add Comment'}
                      </button>
                    </div>
                  )}

                  {/* Comments List */}
                  <div className="divide-y divide-glass-200/10">
                    {comments.length === 0 ? (
                      <p className="text-sm text-glass-400">No comments yet.</p>
                    ) : (
                      [...comments].reverse().map((comment) => (
                        <motion.div
                          key={comment.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="py-3 first:pt-0 last:pb-0"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <CreatorProfile creatorId={comment.author_id} inline />
                              <span className="text-xs text-glass-400 tabular-nums">
                                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                          <MarkdownRenderer content={comment.content} />
                        </motion.div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
            </div>

            {/* Sidebar */}
            <div className="md:w-80 shrink-0 md:overflow-y-auto md:border-l md:border-glass-200/10 md:pl-5 mt-5 md:mt-0">
              <div className="space-y-4">
                {/* Workspaces (mini) */}
                <div className="subcard p-3">
                  <SectionHeader
                    title="Workspaces"
                    icon={KanbanSquare}
                    count={workspacesSummary.length}
                    className="mb-2"
                    actions={incidentId ? (
                      <Link
                        to={`/incidents/${incidentId}`}
                        onClick={onClose}
                        className="text-xs text-accent-300 hover:text-accent-200 hover:underline underline-offset-2"
                        title="Open the full incident page to manage bridge links"
                      >
                        Manage →
                      </Link>
                    ) : undefined}
                  />
                  {workspacesSummary.length === 0 ? (
                    <p className="text-xs text-glass-400">
                      No bridges. Open the full incident page to promote.
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {workspacesSummary.slice(0, 4).map((w) => {
                        const color = kanbanTeamColor(w.board.team);
                        return (
                          <Link
                            key={w.card.id}
                            to={`/workspaces/${encodeURIComponent(w.board.id)}?card=${encodeURIComponent(w.card.id)}`}
                            onClick={onClose}
                            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-glass-200/10 border border-glass-200/10 hover:border-glass-200/20"
                          >
                            <span
                              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: color }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-white truncate">{w.card.title}</div>
                              <div className="text-glass-400 truncate">
                                {w.board.title} · {w.card.list_title}
                              </div>
                            </div>
                            <ExternalLink className="w-3 h-3 text-glass-500 flex-shrink-0" />
                          </Link>
                        );
                      })}
                      {workspacesSummary.length > 4 && incidentId && (
                        <Link
                          to={`/incidents/${incidentId}`}
                          onClick={onClose}
                          className="block text-xs text-center text-glass-400 hover:text-accent-300 py-1"
                        >
                          + {workspacesSummary.length - 4} more
                        </Link>
                      )}
                    </div>
                  )}
                </div>

                {/* Staff Assignees (mini) */}
                {incidentId && (
                  <IncidentAssigneesSection
                    incidentId={incidentId}
                    canManage={canManage}
                    useSync={useSync}
                    compact
                  />
                )}

                {/* Linked Items */}
                <div className="subcard p-3">
                  <SectionHeader
                    title="Items"
                    icon={FileText}
                    count={linkedItems.length}
                    className="mb-2"
                    actions={canManage ? (
                      <button onClick={() => setIsItemLinkModalOpen(true)} className="btn-icon btn-icon-sm" title="Link item" aria-label="Link item">
                        <Plus className="w-4 h-4" />
                      </button>
                    ) : undefined}
                  />
                  {linkedItems.length > 0 ? (
                    <div className="divide-y divide-glass-200/10">
                      {linkedItems.map((item) => (
                        <div key={item.item_id} className="py-2 flex items-center justify-between gap-2 text-sm">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {getItemIcon(item.item_type)}
                            <div className="min-w-0 flex-1">
                              <span className="text-white capitalize">{item.item_type}</span>
                              <span className="text-glass-400 font-mono text-xs truncate block">{item.item_id}</span>
                            </div>
                          </div>
                          {canManage && (
                            <button
                              onClick={() => handleUnlinkItem(item.item_id)}
                              className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                              title="Unlink"
                              aria-label="Unlink item"
                            >
                              <Unlink className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-glass-400 text-xs">No items linked.</p>
                  )}
                </div>

                {/* Linked Users */}
                <div className="subcard p-3">
                  <SectionHeader
                    title="Users"
                    icon={Users}
                    count={linkedUsers.length}
                    className="mb-2"
                    actions={canManage ? (
                      <button onClick={() => setIsUserLinkModalOpen(true)} className="btn-icon btn-icon-sm" title="Link user" aria-label="Link user">
                        <Plus className="w-4 h-4" />
                      </button>
                    ) : undefined}
                  />
                  {linkedUsers.length > 0 ? (
                    <div className="divide-y divide-glass-200/10">
                      {linkedUsers.map((user) => (
                        <div key={user.user_uuid} className="py-2 flex items-center justify-between gap-2 text-sm">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <PlayerProfile uuid={user.user_uuid} showUuid={false} showAvatar={true} inline={true} />
                          </div>
                          {canManage && (
                            <button
                              onClick={() => handleUnlinkUser(user.user_uuid)}
                              className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                              title="Unlink"
                              aria-label="Unlink user"
                            >
                              <Unlink className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-glass-400 text-xs">No users linked.</p>
                  )}
                </div>

                {/* Evidence */}
                <div className="subcard p-3">
                  <SectionHeader
                    title="Evidence"
                    icon={File}
                    count={evidence.length}
                    className="mb-2"
                    actions={canManage ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setIsLinkEvidenceOpen(true)}
                          className="btn-icon btn-icon-sm"
                          title="Link Existing Evidence"
                          aria-label="Link existing evidence"
                        >
                          <Paperclip className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setUploadConfidential(!uploadConfidential)}
                          className={cn(
                            "btn-icon btn-icon-sm",
                            uploadConfidential && "bg-amber-500/15 text-amber-300 border-amber-500/25"
                          )}
                          title={uploadConfidential ? 'Uploads will be confidential' : 'Uploads will be public'}
                          aria-label={uploadConfidential ? 'Uploads will be confidential' : 'Uploads will be public'}
                          aria-pressed={uploadConfidential}
                        >
                          <Lock className="w-4 h-4" />
                        </button>
                        <label className="btn-icon btn-icon-sm cursor-pointer" title="Upload New Evidence" aria-label="Upload new evidence">
                          <Upload className="w-4 h-4" />
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleUploadEvidence(file);
                            }}
                            disabled={isUploadingEvidence}
                          />
                        </label>
                      </>
                    ) : undefined}
                  />
                  {evidence.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {evidence.map((item) => {
                        const redacted = isEvidenceRedacted(item, identity);
                        return (
                        <div key={item.id} className="relative group" title={item.filename}>
                          {redacted ? (
                            <div className="w-full aspect-square rounded-md overflow-hidden">
                              <ConfidentialEvidencePlaceholder compact />
                            </div>
                          ) : item.content_type.startsWith('image/') ? (
                            <img
                              src={getEvidenceViewUrl(item.id)}
                              alt={item.filename}
                              className="w-full aspect-square object-cover rounded-md cursor-pointer"
                              onClick={() => setSelectedImage(item)}
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full aspect-square bg-glass-200/10 rounded-md flex items-center justify-center cursor-pointer" onClick={() => window.open(getEvidenceViewUrl(item.id), '_blank')}>
                              <File className="w-8 h-8 text-glass-400" />
                            </div>
                          )}
                          {!redacted && (
                            <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-md">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (item.content_type.startsWith('image/')) {
                                    setSelectedImage(item);
                                  } else {
                                    window.open(getEvidenceViewUrl(item.id), '_blank');
                                  }
                                }}
                                className="p-2 rounded-full bg-glass-200/20 hover:bg-blue-500/50 text-white"
                                title="View"
                                aria-label="View evidence"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {canManage && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnlinkEvidence(item.id);
                                  }}
                                  className="p-2 rounded-full bg-glass-200/20 hover:bg-red-500/50 text-white"
                                  title="Delete"
                                  aria-label="Delete evidence"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          )}
                          {item.confidential && !redacted && (
                            <div className="absolute top-1 right-1">
                              <ConfidentialBadge />
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-glass-400 text-xs">No evidence uploaded.</p>
                  )}
                </div>

                {/* Linked Tickets */}
                <div className="subcard p-3">
                  <SectionHeader
                    title="Tickets"
                    icon={Ticket}
                    count={tickets.length}
                    className="mb-2"
                    actions={canManage ? (
                      <button onClick={handleLinkTicket} disabled={isLinkingTicket || !newTicketId.trim()} className="btn-icon btn-icon-sm" title="Link Ticket" aria-label="Link ticket">
                        <Plus className="w-4 h-4" />
                      </button>
                    ) : undefined}
                  />
                  {canManage && (
                    <div className="mb-2">
                      <input
                        type="number"
                        value={newTicketId}
                        onChange={(e) => setNewTicketId(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleLinkTicket()}
                        placeholder="Ticket ID"
                        className="glass-input w-full"
                        aria-label="Ticket ID"
                      />
                    </div>
                  )}
                  {tickets.length > 0 ? (
                    <div className="divide-y divide-glass-200/10">
                      {tickets.map((ticket) => (
                        <div key={ticket.ticket_id} className="py-2 flex items-center justify-between gap-2 text-sm">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Ticket className="w-4 h-4 text-blue-400 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <TicketIdLink ticketId={ticket.ticket_id} className="text-white font-medium hover:text-accent-300 tabular-nums">
                                #{ticket.ticket_id}
                              </TicketIdLink>
                              <span className="text-glass-400 ml-2 text-xs tabular-nums">
                                {formatDistanceToNow(new Date(ticket.linked_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                          {canManage && (
                            <button
                              onClick={() => handleUnlinkTicket(ticket.ticket_id)}
                              className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                              title="Unlink"
                              aria-label="Unlink ticket"
                            >
                              <Unlink className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-glass-400 text-xs">No tickets linked.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Image Lightbox */}
      {selectedImage && !isEvidenceRedacted(selectedImage, identity) && (
        <div className="modal-overlay z-[60]" onClick={() => setSelectedImage(null)}>
          <img
            src={getEvidenceViewUrl(selectedImage.id)}
            alt={selectedImage.filename}
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
          <button className="absolute top-4 right-4 text-white hover:text-gray-300" onClick={() => setSelectedImage(null)} title="Close" aria-label="Close">
            <X className="w-8 h-8" />
          </button>
        </div>
      )}

      {/* Modals */}
      {showDeleteConfirm && (
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Incident"
          message="Are you sure you want to delete this incident? This action cannot be undone."
          confirmText="Delete"
          isLoading={isDeleting}
        />
      )}

      {isEditing && incident && (
        <EditIncidentModal
          incident={incident}
          useSync={useSync}
          onClose={() => setIsEditing(false)}
          onSave={(_updatedIncident) => {
            setIsEditing(false);
            fetchData();
            onUpdate?.();
          }}
        />
      )}

      {isUserLinkModalOpen && incidentId && (
        <LinkUserModal
          isOpen={isUserLinkModalOpen}
          onClose={() => setIsUserLinkModalOpen(false)}
          incidentId={incidentId}
          onSuccess={() => {
            setIsUserLinkModalOpen(false);
            fetchData();
          }}
        />
      )}

      {isItemLinkModalOpen && incidentId && (
        <LinkItemModal
          isOpen={isItemLinkModalOpen}
          onClose={() => setIsItemLinkModalOpen(false)}
          incidentId={incidentId}
          onSuccess={() => {
            setIsItemLinkModalOpen(false);
            fetchData();
          }}
        />
      )}

      {isLinkEvidenceOpen && incidentId && (
        <LinkExistingEvidenceModal
          isOpen={isLinkEvidenceOpen}
          onClose={() => setIsLinkEvidenceOpen(false)}
          onLink={handleLinkExistingEvidence}
          excludeEvidenceIds={evidence.map(e => e.id)}
        />
      )}
    </AnimatePresence>
  );
};

